import { Component, Input, Output, EventEmitter, OnInit, OnChanges, OnDestroy, SimpleChanges} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { takeUntil, distinctUntilChanged } from 'rxjs/operators';
import Swal from 'sweetalert2';

import { IProgram, ProgramDoc } from 'src/app/core/domain/program.model';
import { IGlobalSettings } from 'src/app/core/domain/settings.model';
import { getGlobalSettings } from 'src/app/core/state/global';
import { ProgramOfficeService } from 'src/app/core/service/program-office.service';
import { ToastAlertService } from '../utils/toast.service';
import { NgxUiLoaderService } from 'ngx-ui-loader';

@Component({
  selector: 'app-dynamic-upload-documents',
  templateUrl: './dynamic-upload-documents.component.html',
  styleUrls: ['./dynamic-upload-documents.component.scss']
})
export class DynamicUploadDocumentsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() dyanmicDocuments!: IProgram;
  @Input() submission!: number;
  @Input() currentRoundId!: number;
  @Input() isProgramDocument: ProgramDoc[] = [];
  @Input() validationCheckClicked = false;
  @Output() pendingCountChange = new EventEmitter<{ count: number; titles: string[], documents: { fileName: string; attachment: string }[] }>();
  @Output() allHaveAttachments = new EventEmitter<boolean>();

  globalSettings$!: Observable<IGlobalSettings>;
  globalSettings!: IGlobalSettings;
  selectedFile: File[] = [];
  Application_Program: any;
  ReJected_Document='';
  private destroyed$ = new Subject<void>();

  constructor(
    private readonly store: Store,
    private toast: ToastAlertService,
    private service: ProgramOfficeService,
    private router: Router,
    private loader: NgxUiLoaderService,
    private route: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    this.isProgramDocument=[]
     this.ReJected_Document =this.route.snapshot.paramMap.get('re-upload-documents')
    this.globalSettings$ = this.store.pipe(select(getGlobalSettings));
    this.globalSettings$
      .pipe(distinctUntilChanged(), takeUntil(this.destroyed$))
      .subscribe(res => (this.globalSettings = res));
    
    this.onPreDocumentation(this.dyanmicDocuments.id, this.submission, this.currentRoundId);
    this.initializeDocuments();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isProgramDocument'] && !changes['isProgramDocument'].firstChange) {
      this.initializeDocuments();
    }
  }

  initializeDocuments(): void {
    this.isProgramDocument?.forEach(document => {
      const sub = document.submission;
      if (sub?.fileName && sub?.attachment) {
        document.extension = sub.fileName.split('.').pop();
        document.url = { objectUrl: sub.attachment };
      }
    });
    this.emitPendingCount();
  }

  emitPendingCount(): void {
    const pending = this.isProgramDocument?.filter(j => j.isMandatory && this.isSubmissionEmpty(j.submission)) || [];
    const uploaded_Documents = this.isProgramDocument?.filter(k => k.submission?.attachment);
    this.pendingCountChange.emit({
      count: pending.length,
      titles: pending.map(j => j.name),
      documents: uploaded_Documents.map(k => ({
        fileName: k.name ?? '',
        attachment: k.submission?.attachment ?? ''
      }))
    });
  }

  getAcceptTypes(document: any,type:string): string {
  return  document.allowedFileTypes? document.allowedFileTypes.map(type => `.${type}`).join(','):  type === 'image'
      ? '.png,.jpg,.jpeg,.webp'
      : '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip';
}

 supportingFileBrowseHandler(event: any, document: any): void {
    this.selectedFile = [];
    const files = event.target.files;
    if (!files?.length) return;

    if (files.length > 10) {
      this.toast.showToast('Maximum 10 files can be selected', 'error');
      return;
    }

    for (const file of files) {
      if (this.validateFiles([file], document)) {
        this.selectedFile.push(file);
        this.uploadFile(document, file);
      }
    }
    event.target.value = null;
  }

  validateFiles(files: File[], document: any): boolean {
    try {
      files.forEach(f => this.validateSize(f, document.size));
      return true;
    } catch (error: any) {
      this.toast.showToast(error.message, 'error');
      return false;
    }
  }

  validateSize(file: File, maxSizeMB: number): void {
    const sizeInMB = +(file.size / (1024 * 1024)).toFixed(2);
    if (sizeInMB > maxSizeMB) throw new Error(`File size should be equal or less than ${maxSizeMB} MB`);
  }

  uploadFile(document: any, file: File): void {
    document.extension = file.name.split('.').pop();

    const reader = new FileReader();
    reader.onload = () => {
      document.url = { objectUrl: reader.result };
      this.fileSubmit({
        file,
        documentTypeId: document.id,
        programId: this.dyanmicDocuments?.id,
        programRoundId: this.currentRoundId,
        fileName: file.name,
        submissionId: this.submission,
        submission: document.submission
      });
    };
    reader.readAsDataURL(file);
  }

  fileSubmit(event: any): void {
    const { file, documentTypeId, programId, programRoundId, fileName, submissionId, submission } = event;
    const docId = submission?.id;
    const uuid = this.dyanmicDocuments?.uuid ?? this.Application_Program?.uuid;

    this.loader.start('isLoader');
    this.service.uploadProgramDocumentOnS3(uuid, file).subscribe({
      next: res => {
        const payload = {
          fileName,
          attachment: res.data,
          ...(docId
            ? { approvalStatus: 'submitted' }
            : { documentTypeId, programId, programRoundId, submissionId })
        };

        const request$ = docId
          ? this.service.updateDocument(docId, payload)
          : this.service.onSubmitDocuments(payload);

        request$.subscribe({
          next: (res: any) => {
            const { programId, submissionId, programRoundId } = res.data ?? res;
            this.onPreDocumentation(programId, submissionId, programRoundId);
          },
          error: err => this.toast.showToast(err.message, 'error')
        });
        this.loader.stop('isLoader');
        this.toast.showToast(res.message);
      },
      error: err => {
        this.loader.stop('isLoader');
        this.toast.showToast(err.error?.message || 'Upload failed', 'error');
      }
    });
  }

  onPreDocumentation(programId: number, submissionId: number, roundId?: number): void {
  const isReupload = this.ReJected_Document === 're-upload-documents';

  const request$ = isReupload ? this.service.getRejectedDocumentsData(programId, submissionId) : this.service.getPreDocumentsData(programId, submissionId, roundId);

  request$.subscribe({
    next: res => {
      this.isProgramDocument = isReupload ? res.documents?.filter(doc => doc.submission?.approvalStatus === 'rejected') : res.documents;

      this.Application_Program = res.program;
      this.initializeDocuments();
      this.emitPendingCount();

      const { programTitle, programCode } = res.program;
      const kebabTitle = this.toKebabCase(programTitle);

      const allApprovedOrPending = !this.isProgramDocument.some(
        doc => doc.submission?.approvalStatus === 'rejected'
      );
      const allAttached = this.isProgramDocument.every(
        doc => doc.submission?.attachment
      );
      this.allHaveAttachments.emit(allAttached);

      if (allApprovedOrPending) {
        setTimeout(() => {
          this.router.navigate([`/programs/apply/${programCode}/${kebabTitle}`]);
        }, 1000);
      }
    },
    error: err => this.toast.showToast(err.message, 'error')
  });
}

 
  toKebabCase(input: string): string {
    return input?.toLowerCase().replace(/[^\w\s-]/g, '').trim().replace(/\s+/g, '-');
  }

  handleDelete(document: any): void {
    const id = document.submission?.id;
    if (!id) return;

    Swal.fire({
      title: 'Delete Document',
      text: 'Are you sure want to remove this file?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: 'var(--color-theme)',
      cancelButtonColor: '#7D8185',
      confirmButtonText: 'Yes, delete it!'
    }).then(res => {
      if (res.isConfirmed) {
        this.service.deleteDocument(id).subscribe({
          next: (res: any) => {
            const { programId, submissionId, programRoundId } = res.data;
            this.onPreDocumentation(programId, submissionId, programRoundId);
          },
          error: () => this.toast.showToast('Failed to delete', 'error')
        });
      }
    });
  }

  hasRejectedFile(): boolean {
    return this.isProgramDocument.some(j => j.submission?.approvalStatus === 'rejected');
  }

  isSubmissionEmpty(submission: any): boolean {
    return !submission || Object.keys(submission).length === 0;
  }

  downloadFile(url: string, filename: string): void {
    const s3Url = this.globalSettings?.imgKitUrl + url;
    fetch(s3Url)
      .then(res => {
        if (!res.ok) throw new Error('Network response was not ok');
        return res.blob();
      })
      .then(blob => {
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = filename;
        document.body.append(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(blobUrl);
      })
      .catch(err => console.error('Download failed:', err));
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
